import { ROOT_ID } from "../utils";

import type { UUID } from "../../../utils/uuid";
import type { Asset } from "../server/digital-asset-service";
import type { TreeViewItem } from "../utils";
import type { ContentLibraryAction } from "./actions";

export interface ContentLibraryState {
  // UI State
  sidebarOpen: boolean;
  expandedItems: string[];
  isSelectionMode: boolean;
  selectedAssetIds: UUID[];
  selectionAnchorAssetId: UUID | null;

  // Filter State (selected in filter panels, not yet applied)
  selectedContentTypes: string[];
  selectedTags: string[];

  // Applied Filters (active filters affecting the view)
  appliedContentTypes: string[];
  appliedTags: string[];

  // Search
  searchText: string;

  // Folder Selection
  selectedTreeItemId: string | null;

  // Preview
  previewAssetId: string | null;
}

export const initialState: ContentLibraryState = {
  sidebarOpen: true,
  expandedItems: [ROOT_ID],
  isSelectionMode: false,
  selectedAssetIds: [],
  selectionAnchorAssetId: null,
  selectedContentTypes: [],
  selectedTags: [],
  appliedContentTypes: [],
  appliedTags: [],
  searchText: "",
  selectedTreeItemId: null,
  previewAssetId: null,
};

export function contentLibraryReducer(
  state: ContentLibraryState,
  action: ContentLibraryAction,
): ContentLibraryState {
  switch (action.type) {
    case "TOGGLE_SIDEBAR":
      return { ...state, sidebarOpen: !state.sidebarOpen };

    case "SET_SIDEBAR_OPEN":
      return { ...state, sidebarOpen: action.payload };

    case "SET_EXPANDED_ITEMS":
      return { ...state, expandedItems: action.payload };

    case "TOGGLE_SELECTION_MODE": {
      const nextSelectionMode = !state.isSelectionMode;
      if (!nextSelectionMode) {
        return {
          ...state,
          isSelectionMode: false,
          selectedAssetIds: [],
          selectionAnchorAssetId: null,
        };
      }
      return { ...state, isSelectionMode: true };
    }

    case "TOGGLE_ASSET_SELECTION":
      return state.selectedAssetIds.includes(action.payload)
        ? {
            ...state,
            selectedAssetIds: state.selectedAssetIds.filter((id) => id !== action.payload),
          }
        : {
            ...state,
            selectedAssetIds: [...state.selectedAssetIds, action.payload],
          };

    case "SELECT_ASSET_RANGE": {
      const nextIds = action.payload.append
        ? Array.from(new Set([...state.selectedAssetIds, ...action.payload.assetIds]))
        : action.payload.assetIds;
      return {
        ...state,
        selectedAssetIds: nextIds,
      };
    }

    case "SET_SELECTION_ANCHOR":
      return {
        ...state,
        selectionAnchorAssetId: action.payload,
      };

    case "CLEAR_ASSET_SELECTION":
    case "CLEAR_SELECTION_ON_CONTEXT_CHANGE":
      return {
        ...state,
        selectedAssetIds: [],
        selectionAnchorAssetId: null,
      };

    case "SET_SELECTED_CONTENT_TYPES":
      return { ...state, selectedContentTypes: action.payload };

    case "SET_SELECTED_TAGS":
      return { ...state, selectedTags: action.payload };

    case "APPLY_FILTERS":
      return {
        ...state,
        appliedContentTypes: state.selectedContentTypes,
        appliedTags: state.selectedTags,
      };

    case "REMOVE_CONTENT_TYPE_FILTER":
      return {
        ...state,
        appliedContentTypes: state.appliedContentTypes.filter((ct) => ct !== action.payload),
        selectedContentTypes: state.selectedContentTypes.filter((ct) => ct !== action.payload),
      };

    case "REMOVE_TAG_FILTER":
      return {
        ...state,
        appliedTags: state.appliedTags.filter((tag) => tag !== action.payload),
        selectedTags: state.selectedTags.filter((tag) => tag !== action.payload),
      };

    case "SET_SEARCH_TEXT":
      return { ...state, searchText: action.payload };

    case "SELECT_FOLDER":
      return { ...state, selectedTreeItemId: action.payload };

    case "SYNC_FROM_URL":
      return {
        ...state,
        appliedContentTypes: action.payload.contentTypes,
        appliedTags: action.payload.tags,
        selectedContentTypes: action.payload.contentTypes,
        selectedTags: action.payload.tags,
        previewAssetId: action.payload.previewAssetId,
        selectedTreeItemId: action.payload.selectedFolderId,
      };

    case "SET_PREVIEW_ASSET_ID":
      return { ...state, previewAssetId: action.payload };

    default:
      return state;
  }
}

export function filterAssets(
  assets: Asset[],
  state: {
    selectedTreeItemId: string | null;
    appliedContentTypes: string[];
    appliedTags: string[];
    searchText: string;
  },
  treeItems: TreeViewItem[],
): Asset[] {
  let filtered = assets;

  // Filter by folder
  if (state.selectedTreeItemId !== null) {
    const findItem = (items: TreeViewItem[]): TreeViewItem | undefined => {
      for (const item of items) {
        if (item.id === state.selectedTreeItemId) return item;
        if (item.children) {
          const found = findItem(item.children);
          if (found) return found;
        }
      }
      return undefined;
    };
    const folderItem = findItem(treeItems);
    if (folderItem) {
      filtered = filtered.filter((asset) => asset.folder_id === folderItem.folderId);
    }
  }

  // Filter by content types
  if (state.appliedContentTypes.length > 0) {
    filtered = filtered.filter((asset) => state.appliedContentTypes.includes(asset.content_type));
  }

  // Filter by tags
  if (state.appliedTags.length > 0) {
    filtered = filtered.filter((asset) =>
      asset.tags.some((tag) => state.appliedTags.includes(tag)),
    );
  }

  // Filter by search text
  if (state.searchText.trim()) {
    const searchLower = state.searchText.trim().toLowerCase();
    filtered = filtered.filter(
      (asset) =>
        asset.title.toLowerCase().includes(searchLower) ||
        asset.content_type.toLowerCase().includes(searchLower),
    );
  }

  return filtered;
}
